/* Muted autoplay with accessible play/pause and optional sound controls. */
(() => {
  if (customElements.get('atlas-event-hero-media')) return;

  class AtlasEventHeroMedia extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.startupController) return;
      this.startupController = new AbortController();
      const startup = this.startupController;
      const queue = () => {
        cancelAnimationFrame(this.startupFrame);
        this.startupFrame = requestAnimationFrame(() => {
          if (!this.isConnected || this.startupController !== startup || document.readyState === 'loading') return;
          const video = this.querySelector('[data-ael-hero-video]');
          if (!video) return;
          this.startupObserver.disconnect();
          startup.abort();
          this.startupController = null;
          this.initialize(video);
        });
      };
      this.startupObserver = new MutationObserver(queue);
      this.startupObserver.observe(this, {childList: true, subtree: true});
      document.addEventListener('DOMContentLoaded', queue, {once: true, signal: startup.signal});
      queue();
    }

    initialize(video) {
      if (this.controller) return;
      this.controller = new AbortController();
      const sameVideo = this.video === video;
      this.video = video;
      this.toggle = this.querySelector('[data-ael-video-toggle]');
      this.playLabel = this.toggle?.querySelector('[data-ael-play-label]');
      this.pauseLabel = this.toggle?.querySelector('[data-ael-pause-label]');
      this.soundToggle = this.querySelector('[data-ael-sound-toggle]');
      this.unmuteLabel = this.soundToggle?.querySelector('[data-ael-unmute-label]');
      this.muteLabel = this.soundToggle?.querySelector('[data-ael-mute-label]');
      // Muting is required for silent inline autoplay. Other playback options
      // come from the section settings represented by the native attributes.
      video.defaultMuted = true;
      if (!sameVideo) {
        video.muted = true;
        this.userPaused = false;
        this.audibleVolume = video.volume > 0 ? video.volume : 1;
      }
      video.playsInline = true;
      this.listen(video, 'play', () => { if (!video.paused && !video.ended) this.userPaused = false; this.render(); });
      this.listen(video, 'playing', () => this.render());
      this.listen(video, 'pause', () => { this.userPaused = true; this.render(); });
      this.listen(video, 'ended', () => this.render());
      this.listen(video, 'loadedmetadata', () => this.render());
      this.listen(video, 'error', () => this.render());
      this.listen(video, 'volumechange', () => {
        if (video.volume > 0) this.audibleVolume = video.volume;
        this.render();
      });
      this.listen(this.toggle, 'click', () => {
        if (video.paused || video.ended) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          video.pause();
          this.render();
        }
      });
      this.listen(this.soundToggle, 'click', () => {
        if (video.muted || video.volume === 0) {
          if (video.volume === 0) video.volume = this.audibleVolume || 1;
          video.muted = false;
        } else {
          video.muted = true;
        }
        this.render();
      });

      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || this.userPaused) {
        video.pause();
      } else if (video.hasAttribute('autoplay')) {
        this.play();
      }
      this.render();
    }

    listen(target, type, handler) {
      target?.addEventListener(type, handler, {signal: this.controller.signal});
    }

    play() {
      try {
        const pending = this.video.play();
        pending?.catch(() => this.render());
      } catch (_) {
        this.render();
      }
    }

    render() {
      if (!this.video) return;
      const playing = !this.video.paused && !this.video.ended;
      if (this.toggle) {
        this.toggle.hidden = !!this.video.error;
        this.toggle.setAttribute('aria-pressed', String(playing));
      }
      if (this.playLabel) this.playLabel.hidden = playing;
      if (this.pauseLabel) this.pauseLabel.hidden = !playing;
      if (this.soundToggle) {
        const audible = !this.video.muted && this.video.volume > 0;
        this.soundToggle.hidden = !!this.video.error;
        this.soundToggle.setAttribute('aria-pressed', String(audible));
        if (this.unmuteLabel) this.unmuteLabel.hidden = audible;
        if (this.muteLabel) this.muteLabel.hidden = !audible;
      }
    }

    disconnectedCallback() {
      this.startupController?.abort();
      this.startupController = null;
      this.startupObserver?.disconnect();
      cancelAnimationFrame(this.startupFrame);
      this.controller?.abort();
      this.controller = null;
      this.video?.pause();
      if (this.toggle) this.toggle.hidden = true;
      if (this.soundToggle) this.soundToggle.hidden = true;
    }
  }

  customElements.define('atlas-event-hero-media', AtlasEventHeroMedia);
})();
